import React, { useState, useEffect, useCallback, useRef } from 'react'
import { designTokens } from '@/utils/design-tokens'
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react'
import { AcademyinfoService } from '@/api/generated/services/AcademyinfoService'
import type { AcademyListItem } from '@/api/generated/models/AcademyListItem'
import type { DepartmentListResponse } from '@/api/generated/models/DepartmentListResponse'

interface DepartmentDataTabProps {
  myAcademy: AcademyListItem
  selectedYear: string
  availableYears: string[]
  onYearChange: (year: string) => void
  tabSwitcher?: React.ReactNode
}

const DepartmentDataTab: React.FC<DepartmentDataTabProps> = ({
  myAcademy,
  selectedYear,
  availableYears,
  onYearChange,
  tabSwitcher,
}) => {
  const [selectedAcademy, setSelectedAcademy] = useState<AcademyListItem>(myAcademy)
  const [deptData, setDeptData] = useState<DepartmentListResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({})

  // Academy list & search state
  const [allAcademies, setAllAcademies] = useState<AcademyListItem[]>([])
  const [academySearchQuery, setAcademySearchQuery] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Fetch all academies once
  useEffect(() => {
    const fetchAllAcademies = async () => {
      try {
        const response = await AcademyinfoService.appsAcademyinfoViewsListAcademies({ perPage: 1000 })
        setAllAcademies(response.data || [])
      } catch (err) {
        console.error('Failed to fetch academies:', err)
      }
    }
    fetchAllAcademies()
  }, [])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredAcademies = academySearchQuery.trim()
    ? allAcademies.filter(a =>
        a.name.includes(academySearchQuery) ||
        (a.region && a.region.includes(academySearchQuery))
      )
    : allAcademies

  const handleSelectAcademy = (academy: AcademyListItem) => {
    setSelectedAcademy(academy)
    setAcademySearchQuery('')
    setIsDropdownOpen(false)
  }

  const handleResetToMySchool = () => {
    setSelectedAcademy(myAcademy)
    setAcademySearchQuery('')
  }

  const fetchDepartments = useCallback(async () => {
    if (!selectedAcademy?.id || !selectedYear) return
    setIsLoading(true)
    try {
      const response = await AcademyinfoService.appsAcademyinfoViewsListDepartments({
        academyId: selectedAcademy.id,
        surveyYear: selectedYear,
      })
      setDeptData(response)
    } catch (err) {
      console.error('Failed to fetch departments:', err)
    } finally {
      setIsLoading(false)
    }
  }, [selectedAcademy?.id, selectedYear])

  useEffect(() => {
    fetchDepartments()
  }, [fetchDepartments])

  const toggleCategory = (category: string) => {
    setCollapsedCategories(prev => ({ ...prev, [category]: !prev[category] }))
  }

  // Extract column definitions from the first department's indicators
  const indicatorColumns = React.useMemo(() => {
    if (!deptData) return []
    for (const cat of deptData.categories) {
      for (const dept of cat.departments) {
        if (dept.indicators && dept.indicators.length > 0) {
          return dept.indicators.map(ind => ({
            flag: ind.flag,
            label: ind.label || ind.flag,
            unit: ind.unit || '',
          }))
        }
      }
    }
    return []
  }, [deptData])

  const getDeptIndicatorValue = (
    indicators: { flag: string; value?: string; value2?: string; label?: string }[] | undefined,
    flag: string
  ): string => {
    if (!indicators) return '-'
    const item = indicators.find(i => i.flag === flag)
    if (!item?.value) return '-'
    return item.value
  }

  const cellStyle: React.CSSProperties = {
    padding: '8px 12px',
    fontFamily: designTokens.typography.fontFamily.sans[0],
    fontSize: '14px',
    color: designTokens.colors.gray[6],
    borderBottom: `1px solid ${designTokens.colors.gray[1]}`,
    whiteSpace: 'nowrap',
  }

  const headerCellStyle: React.CSSProperties = {
    ...cellStyle,
    fontWeight: 600,
    fontSize: '13px',
    backgroundColor: designTokens.colors.gray[0],
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
  }

  return (
    <div className="h-full flex flex-col">
      {/* Info Bar */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          {tabSwitcher}
          <span
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '16px',
              fontWeight: 600,
              color: designTokens.colors.gray[6],
            }}
          >
            학과별 자료
          </span>

          {/* Academy selector */}
          <div className="relative" ref={dropdownRef}>
            <div
              className="flex items-center gap-2 px-3 rounded border"
              style={{
                borderColor: designTokens.colors.gray[2],
                height: '32px',
                minWidth: '220px',
              }}
            >
              <Search className="w-3.5 h-3.5 flex-shrink-0" style={{ color: designTokens.colors.gray[4] }} />
              <input
                type="text"
                value={isDropdownOpen ? academySearchQuery : ''}
                onChange={e => setAcademySearchQuery(e.target.value)}
                onFocus={() => setIsDropdownOpen(true)}
                placeholder={selectedAcademy.name + (selectedAcademy.id === myAcademy.id ? ' (우리학교)' : '')}
                className="flex-1 outline-none bg-transparent"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '13px',
                  color: designTokens.colors.gray[6],
                  border: 'none',
                  minWidth: '120px',
                }}
              />
              {selectedAcademy.id !== myAcademy.id && (
                <button
                  onClick={handleResetToMySchool}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: designTokens.colors.gray[4], padding: 0 }}
                  title="우리학교로 초기화"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Dropdown */}
            {isDropdownOpen && (
              <div
                className="absolute left-0 right-0 mt-1 bg-white rounded border shadow-lg z-10 max-h-[240px] overflow-auto"
                style={{ borderColor: designTokens.colors.gray[2], minWidth: '280px' }}
              >
                {filteredAcademies.length > 0 ? (
                  filteredAcademies.map(academy => (
                    <button
                      key={academy.id}
                      type="button"
                      onClick={() => handleSelectAcademy(academy)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors"
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '13px',
                        color: designTokens.colors.gray[6],
                        border: 'none',
                        background: academy.id === selectedAcademy.id ? designTokens.colors.gray[0] : 'none',
                        cursor: 'pointer',
                        borderBottom: `1px solid ${designTokens.colors.gray[0]}`,
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>{academy.name}</span>
                      {(academy.establishment_type || academy.region) && (
                        <span style={{ fontSize: '12px', color: designTokens.colors.gray[4], marginLeft: '6px' }}>
                          {[academy.establishment_type, academy.region].filter(Boolean).join(' / ')}
                        </span>
                      )}
                      {academy.id === myAcademy.id && (
                        <span style={{ fontSize: '11px', color: designTokens.colors.primary.DEFAULT, marginLeft: '4px' }}>(우리학교)</span>
                      )}
                    </button>
                  ))
                ) : academySearchQuery.trim() ? (
                  <div className="px-3 py-2 text-center" style={{ fontSize: '13px', color: designTokens.colors.gray[4] }}>
                    검색 결과가 없습니다.
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Year Selector */}
        {availableYears.length > 0 && (
          <div
            className="flex items-center rounded overflow-hidden"
            style={{ border: `1px solid ${designTokens.colors.gray[2]}` }}
          >
            {availableYears.map((year, idx) => (
              <button
                key={year}
                onClick={() => onYearChange(year)}
                style={{
                  padding: '4px 14px',
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '13px',
                  fontWeight: selectedYear === year ? 600 : 400,
                  color: selectedYear === year ? '#fff' : designTokens.colors.gray[5],
                  backgroundColor: selectedYear === year ? designTokens.colors.primary.DEFAULT : '#fff',
                  border: 'none',
                  borderRight: idx < availableYears.length - 1 ? `1px solid ${designTokens.colors.gray[2]}` : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {year}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto rounded border" style={{ borderColor: designTokens.colors.gray[1] }}>
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '14px', color: designTokens.colors.gray[4] }}>
              데이터를 불러오는 중...
            </span>
          </div>
        ) : !deptData || deptData.categories.length === 0 ? (
          <div className="flex items-center justify-center h-48">
            <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '14px', color: designTokens.colors.gray[4] }}>
              학과별 자료가 없습니다.
            </span>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead>
              <tr>
                <th style={{ ...headerCellStyle, width: '220px', textAlign: 'left', position: 'sticky', left: 0, zIndex: 2 }}>
                  학과명
                </th>
                {indicatorColumns.map(col => (
                  <th key={col.flag} style={{ ...headerCellStyle, textAlign: 'right' }}>
                    {col.label}
                    {col.unit && (
                      <span style={{ fontSize: '11px', color: designTokens.colors.gray[4], marginLeft: '2px' }}>
                        ({col.unit})
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deptData.categories.map(category => {
                const isCollapsed = collapsedCategories[category.category]

                return (
                  <React.Fragment key={category.category}>
                    {/* Category Header */}
                    <tr
                      onClick={() => toggleCategory(category.category)}
                      style={{ cursor: 'pointer', backgroundColor: designTokens.colors.gray[0] }}
                    >
                      <td
                        colSpan={indicatorColumns.length + 1}
                        style={{
                          ...cellStyle,
                          fontWeight: 700,
                          fontSize: '14px',
                          padding: '10px 12px',
                          color: designTokens.colors.primary.DEFAULT,
                        }}
                      >
                        <div className="flex items-center gap-1">
                          {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          {category.category}
                          <span style={{ fontWeight: 400, fontSize: '12px', color: designTokens.colors.gray[4], marginLeft: '4px' }}>
                            ({category.departments.length}개 학과)
                          </span>
                        </div>
                      </td>
                    </tr>

                    {/* Department Rows */}
                    {!isCollapsed && category.departments.map(dept => (
                      <tr key={dept.id} className="hover:bg-gray-50">
                        <td style={{ ...cellStyle, paddingLeft: '28px', position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1 }}>
                          {dept.name}
                        </td>
                        {indicatorColumns.map(col => (
                          <td key={col.flag} style={{ ...cellStyle, textAlign: 'right' }}>
                            {getDeptIndicatorValue(dept.indicators, col.flag)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default DepartmentDataTab
